import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  createFilterOptions,
  FormControl,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { formatTaxOfficeLabel } from "../utils/taxOfficeUtils";
import { useTaxOffices } from "../hooks/useTaxOffices";
import CreateTaxOfficeDialog from "./CreateTaxOfficeDialog";
import TaxOfficeDetailsDialog from "./TaxOfficeDetailsDialog";

const actionButtonSx = {
  p: 0.25,
  borderRadius: 0,
  bgcolor: "transparent",
  "&:hover": {
    bgcolor: "transparent",
  },
};

const filterOptions = createFilterOptions({
  stringify: (option) =>
    [option.name, option.taxOfficeNumber, option.city, option.street, option.postalCode]
      .filter(Boolean)
      .join(" "),
});

const TaxOfficeSelect = ({
  name = "taxOfficeId",
  label = "Tax Office",
  value,
  countryId,
  onChange,
  onBlur,
  error,
  helperText,
  required = true,
  disabled = false,
}) => {
  const { taxOffices, isLoading, refreshTaxOffices } = useTaxOffices(countryId);
  const [createdTaxOffices, setCreatedTaxOffices] = useState([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);

  useEffect(() => {
    setCreatedTaxOffices([]);
  }, [countryId]);

  const filteredTaxOffices = [...taxOffices, ...createdTaxOffices].filter(
    (taxOffice, index, offices) =>
      Number(taxOffice.countryId) === Number(countryId) &&
      offices.findIndex((item) => item.taxOfficeId === taxOffice.taxOfficeId) === index
  );
  const selectedTaxOffice =
    filteredTaxOffices.find((taxOffice) => taxOffice.taxOfficeId === Number(value)) || null;
  const hasCountry = Boolean(countryId);
  const isDisabled = disabled || !hasCountry;
  const statusHelperText = !hasCountry
    ? "Select a country first"
    : isLoading
      ? "Loading tax offices..."
      : filteredTaxOffices.length === 0
        ? "No tax offices available for this country"
        : "";

  const handleChange = (_event, newValue) => {
    onChange?.({
      target: {
        name,
        value: newValue?.taxOfficeId || "",
      },
      option: newValue || null,
    });
  };

  const handleCreated = (newTaxOffice) => {
    if (!newTaxOffice?.taxOfficeId) {
      setIsCreateDialogOpen(false);
      return;
    }

    setCreatedTaxOffices((currentOffices) => {
      const exists = currentOffices.some(
        (taxOffice) => taxOffice.taxOfficeId === newTaxOffice.taxOfficeId
      );
      if (exists) {
        return currentOffices;
      }

      return [...currentOffices, newTaxOffice];
    });
    handleChange(null, newTaxOffice);
    refreshTaxOffices();
    setIsCreateDialogOpen(false);
  };

  const canViewTaxOffice = Boolean(selectedTaxOffice);

  const handleViewTaxOffice = () => {
    if (!canViewTaxOffice) {
      return;
    }

    setIsViewDialogOpen(true);
  };

  const handleViewTaxOfficeKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleViewTaxOffice();
    }
  };

  return (
    <Box sx={{ position: "relative", my: 2 }}>
      <Box
        sx={{
          position: "absolute",
          top: -19,
          right: 0,
          zIndex: 2,
          display: "flex",
          alignItems: "center",
        }}
      >
        <Tooltip title="View tax office">
          <span>
            <IconButton
              size="small"
              aria-label="View tax office"
              tabIndex={0}
              onClick={handleViewTaxOffice}
              onKeyDown={handleViewTaxOfficeKeyDown}
              disabled={!canViewTaxOffice}
              disableRipple
              disableFocusRipple
              sx={actionButtonSx}
            >
              <VisibilityIcon
                fontSize="small"
                sx={{ color: canViewTaxOffice ? "primary.main" : "action.disabled" }}
              />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Add tax office">
          <span>
            <IconButton
              size="small"
              aria-label="Add tax office"
              tabIndex={0}
              onClick={() => setIsCreateDialogOpen(true)}
              disabled={isDisabled}
              disableRipple
              disableFocusRipple
              sx={actionButtonSx}
            >
              <AddIcon fontSize="small" sx={{ color: isDisabled ? "action.disabled" : "primary.main" }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <FormControl fullWidth required={required} error={Boolean(error)}>
        <Autocomplete
          id={name}
          options={filteredTaxOffices}
          value={selectedTaxOffice}
          disabled={isDisabled}
          loading={isLoading}
          filterOptions={filterOptions}
          getOptionLabel={(option) => formatTaxOfficeLabel(option)}
          isOptionEqualToValue={(option, selected) => option.taxOfficeId === selected.taxOfficeId}
          onChange={handleChange}
          onBlur={onBlur}
          renderInput={(params) => (
            <TextField
              {...params}
              name={name}
              label={label}
              required={required}
              error={Boolean(error)}
              helperText={helperText || statusHelperText}
              variant="outlined"
              inputProps={{
                ...params.inputProps,
                "aria-label": label,
              }}
            />
          )}
        />
      </FormControl>
      <CreateTaxOfficeDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onCreated={handleCreated}
        defaultCountryId={countryId}
      />
      <TaxOfficeDetailsDialog
        open={isViewDialogOpen}
        onClose={() => setIsViewDialogOpen(false)}
        taxOffice={selectedTaxOffice}
        taxOfficeId={selectedTaxOffice?.taxOfficeId}
      />
    </Box>
  );
};

export default TaxOfficeSelect;
